import {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactElement,
} from "react";
import { MarketingHeader } from "./MarketingHeader";
import { getApiSourceDocApi } from "../../service/points";
import {
  getPublicVoiceTonePageApi,
  resolvePublicVoiceAudioFullUrl,
} from "../../service/voicePublic";
import { getLanguageName } from "../../utils/languageUtils";
import { HomeSeriesPagination } from "../../components/common";
import {
  getMethodClass,
  getGroupEmoji,
  getCardDesc,
  type ApiItem,
  type ApiGroup,
  type ApiDocData,
  type SampleLang,
  type MethodClass,
  type ApiSection,
  type ApiSectionTable,
  type ApiSectionTableRow,
  type VoicePreviewRow,
} from "./apiDocData";
import "./ApiDocPage.scss";
import { SkillDocPage, SkillDocTabBar } from "./SkillDocPage";

/**
 * 图标组件 - 内联 SVG，避免引入额外依赖
 */
const Icon: FC<{ name: string; className?: string }> = ({
  name,
  className,
}) => {
  const paths: Record<string, ReactElement> = {
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    ),
    "chevron-down": <polyline points="6 9 12 15 18 9" />,
    copy: (
      <>
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </>
    ),
    sparkles: (
      <>
        <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z" />
      </>
    ),
    "file-code-2": (
      <>
        <path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4" />
        <polyline points="14 2 14 8 20 8" />
        <path d="m9 14 2 2-2 2" />
        <path d="m13 18 2-2-2-2" />
      </>
    ),
    "alert-triangle": (
      <>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
    lock: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
    "loader-2": (
      <>
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
      </>
    ),
  };

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || null}
    </svg>
  );
};

/**
 * 分组图标组件
 * 统一回退到 getGroupEmoji 返回的 emoji 字符渲染
 */
export const GroupIcon: FC<{ group: ApiGroup }> = ({ group }) => {
  return <>{getGroupEmoji(group.emoji, group.icon, group.title)}</>;
};

/**
 * 可水平滚动的代码区组件
 * - 仅当代码宽度溢出时，滚动条左右两端显示 ◀ ▶ 三角形装饰
 * - 三角形纯视觉装饰，不可点击，与底部滚动条同一水平线
 */
const ScrollableCode: FC<{ code: string }> = ({ code }) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = useState(false);

  /** 检测内容是否溢出容器（scrollWidth > clientWidth） */
  const checkOverflow = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth + 2);
  }, []);

  useEffect(() => {
    checkOverflow();
    // 监听容器尺寸变化，重新计算溢出状态
    const ro = new ResizeObserver(checkOverflow);
    if (bodyRef.current) ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, [checkOverflow, code]);

  return (
    <div className={`api-doc__cb-scroll${overflow ? " has-overflow" : ""}`}>
      <span
        className="api-doc__cb-arrow api-doc__cb-arrow--left"
        aria-hidden="true"
      />
      <div
        className="api-doc__cb-body"
        ref={bodyRef}
        dangerouslySetInnerHTML={{ __html: `<pre>${code}</pre>` }}
      />
      <span
        className="api-doc__cb-arrow api-doc__cb-arrow--right"
        aria-hidden="true"
      />
    </div>
  );
};

/**
 * 转义 HTML 特殊字符，防止代码示例中的特殊字符破坏 DOM
 */
const escapeHtml = (str: string): string => {
  const div = document.createElement("div");
  div.appendChild(document.createTextNode(str || ""));
  return div.innerHTML;
};

/**
 * 简易代码语法高亮：返回 HTML 字符串
 * 高亮规则（编译器风格配色）：
 * - 字符串（'xxx' / "xxx" / `xxx`）→ 浅绿
 * - 注释（// ... / # ...）→ 灰色斜体
 * - 关键字（import/from/const/public/void 等）→ 粉紫
 * - 数字 → 橙色
 * - 函数名（xxx(  )）→ 蓝色
 * - URL（http(s)://...）→ 青色下划线
 * - 变量引用（{xxx} / ${xxx}）→ 黄色
 */
const highlightCode = (code: string, lang: SampleLang): string => {
  if (!code) return "";
  // 先转义，避免 HTML 注入
  let html = escapeHtml(code);
  // 占位符机制：每生成一个高亮 span，立即用占位符替换，避免后续正则误匹配标签属性
  // （如 class 是 python 关键字，会破坏 <span class="hl-url"> 的标签结构）
  const slots: string[] = [];
  const wrap = (content: string, cls: string): string => {
    slots.push(`<span class="${cls}">${content}</span>`);
    return `\u0000PH${slots.length - 1}PH\u0000`;
  };
  // 1) 字符串（'...' / "..." / `...`）—— 需在转义后处理，注意 &quot; &apos;
  html = html.replace(/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;|`[^`]*?`)/g, (m) =>
    wrap(m, "hl-str"),
  );
  // 2) 注释
  if (lang === "python" || lang === "curl") {
    html = html.replace(
      /(^|\n)(\s*#[^\n]*)/g,
      (_m, p1: string, p2: string) => p1 + wrap(p2, "hl-com"),
    );
  } else {
    html = html.replace(/(\/\/[^\n]*)/g, (m) => wrap(m, "hl-com"));
    html = html.replace(/(\/\*[\s\S]*?\*\/)/g, (m) => wrap(m, "hl-com"));
  }
  // 3) URL
  html = html.replace(/(https?:\/\/[^\s'"`&<>]+)/g, (m) => wrap(m, "hl-url"));
  // 4) 关键字
  const keywords =
    lang === "java"
      ? /\b(import|from|public|private|protected|static|final|void|class|interface|extends|implements|new|return|if|else|for|while|try|catch|finally|throw|throws|this|super|null|true|false|int|long|double|float|boolean|String|byte|char|short)\b/g
      : lang === "python"
        ? /\b(import|from|def|class|return|if|elif|else|for|while|try|except|finally|with|as|in|not|and|or|is|None|True|False|self|lambda|yield|raise|pass|break|continue|global|nonlocal|assert|del)\b/g
        : /\b(curl|sudo|export|cd|ls|mkdir|rm|cp|mv|echo|cat|grep|if|then|else|fi|for|do|done|while)\b/g;
  html = html.replace(keywords, (m) => wrap(m, "hl-kw"));
  // 5) 数字
  html = html.replace(/\b(\d+\.?\d*)\b/g, (m) => wrap(m, "hl-num"));
  // 6) 函数调用 xxx(
  html = html.replace(
    /\b([a-zA-Z_]\w*)(\s*\()/g,
    (_m, p1: string, p2: string) => wrap(p1, "hl-fn") + p2,
  );
  // 7) 变量引用 ${xxx} / {xxx}
  html = html.replace(/(\$\{[^}]+\})/g, (m) => wrap(m, "hl-var"));
  // 还原所有占位符为对应的高亮 span
  return html.replace(/\u0000PH(\d+)PH\u0000/g, (_m, i: string) => slots[+i]);
};

/**
 * 判断必填样式
 */
const isRequired = (required: string): boolean => {
  return String(required || "").includes("必填");
};

/**
 * 规范化 sections 表格：兼容"行数组"与"{headers, rows}"两种后端返回形式
 * 返回 { headers, rows, colKeys }，colKeys 为排序后的列字段名（col1/col2/col3...）
 */
const normalizeSectionTable = (
  table: ApiSectionTable | null | undefined,
  sectionTitle?: string,
): {
  headers: string[];
  rows: ApiSectionTableRow[];
  colKeys: string[];
} | null => {
  if (!table) return null;
  // 情况 1：table 直接是行数组
  if (Array.isArray(table)) {
    const rows = table;
    if (rows.length === 0) return null;
    // 从第一行提取列字段名，按 col1/col2/col3 数字升序排序
    const colKeys = Object.keys(rows[0] || {}).sort((a, b) => {
      const na = parseInt(a.replace(/^col/i, ""), 10) || 0;
      const nb = parseInt(b.replace(/^col/i, ""), 10) || 0;
      return na - nb;
    });
    if (colKeys.length === 0) return null;
    // 无显式表头时，按列数+标题给通用表头
    const headers = defaultHeadersForCols(colKeys.length, sectionTitle);
    return { headers, rows, colKeys };
  }
  // 情况 2：table 是 { headers?, rows }
  const rows = table.rows || [];
  const colKeys =
    rows.length > 0
      ? Object.keys(rows[0] || {}).sort((a, b) => {
          const na = parseInt(a.replace(/^col/i, ""), 10) || 0;
          const nb = parseInt(b.replace(/^col/i, ""), 10) || 0;
          return na - nb;
        })
      : (table.headers || []).map((_, i) => `col${i + 1}`);
  if (colKeys.length === 0) return null;
  const headers =
    table.headers && table.headers.length > 0
      ? table.headers
      : defaultHeadersForCols(colKeys.length, sectionTitle);
  return { headers, rows, colKeys };
};

/**
 * 按列数生成默认表头（后端未提供 headers 时使用）
 */
const defaultHeadersForCols = (
  count: number,
  sectionTitle?: string,
): string[] => {
  const title = (sectionTitle || "").trim();
  // 根据标题语义匹配专用表头
  if (count === 2) {
    if (/计费|billing/i.test(title)) return ["接口", "计费"];
    if (/步骤|step/i.test(title)) return ["类型", "说明"];
    // Header 格式（4字节）/ Header 格式 等：字节序号 + 说明
    if (/header|头部|字节|byte/i.test(title)) return ["序号", "说明"];
    // 状态说明：字段 + 状态
    if (/状态说明|状态/i.test(title)) return ["字段", "状态"];
    return ["接口", "说明"];
  }
  if (count === 3) {
    // 常见问题：问题 + 原因 + 处理方式
    if (/常见问题|faq|问题/i.test(title)) return ["问题", "原因", "处理方式"];
    return ["序号", "类型", "说明"];
  }
  if (count === 4) {
    if (/认证|auth/i.test(title)) return ["Key", "说明", "必填", "示例"];
    if (/示例|example/i.test(title))
      return ["位置", "subtitle_x", "subtitle_y", "subtitle_font_size"];
    if (/错误码|error/i.test(title))
      return ["HTTP 状态码", "业务错误码 code", "内容", "说明"];
    return ["字段", "类型", "必填", "说明"];
  }
  return Array.from({ length: count }, (_, i) => `列${i + 1}`);
};

/**
 * 判断 section 是否为"调用示例"类小节
 * 这类小节（含 Java/cURL/Python 代码）只在右侧示例栏展示，不在中间内容区渲染
 */
const isSampleSection = (title: string): boolean => {
  const t = (title || "").trim();
  if (!t) return false;
  // 去掉 md 标题前缀 # 后再判断
  const raw = t.replace(/^#+\s*/, "").trim();
  if (raw === "调用示例") return true;
  if (/^调用示例[\s\-\/]/i.test(raw)) return true;
  if (
    /^(java|python|curl|bash|node\.?js|go|javascript|typescript|php|ruby|c#|c\+\+|shell)$/i.test(
      raw,
    )
  )
    return true;
  return false;
};

/**
 * 从 sections 中按语言名提取调用示例代码（标题为 Java/Python/cURL 等的小节 content）
 * 自动剥离 md 代码块标记（```lang ... ```）和 ``` 字符，只保留纯代码
 */
const sampleFromSections = (
  activeItem: ApiItem | null,
  lang: SampleLang,
): string => {
  if (!activeItem?.sections || activeItem.sections.length === 0) return "";
  const langTitleMap: Record<SampleLang, RegExp> = {
    curl: /^(curl|bash|shell)$/i,
    python: /^python$/i,
    java: /^java$/i,
  };
  for (const sec of activeItem.sections) {
    const t = (sec.title || "").trim();
    if (langTitleMap[lang].test(t) && sec.content) {
      return stripCodeFence(String(sec.content)).trim();
    }
  }
  return "";
};

/**
 * 剥离 md 代码块标记（```lang ... ``` 或单行 ```），只保留纯代码内容
 * 兼容：首行 ```Bash、首尾各一行 ```、无代码块标记的纯代码
 */
const stripCodeFence = (raw: string): string => {
  if (!raw) return "";
  const text = raw.trim();
  // 整段被 ``` 包裹：```lang\n...\n```
  const fenceMatch = text.match(/^```[a-zA-Z]*\s*\n([\s\S]*?)\n```\s*$/);
  if (fenceMatch) return fenceMatch[1];
  // 仅首行 ```lang，去掉首尾 ``` 行
  const lines = text.split("\n");
  const filtered = lines.filter(
    (line) => !/^```[a-zA-Z]*\s*$/.test(line.trim()),
  );
  return filtered.join("\n");
};

/**
 * md 行内格式渲染：加粗 **xxx** / 行内代码 `xxx` / 链接 [text](url)
 * 输入需已转义，输出可直接拼入 HTML
 */
const renderMdInline = (text: string): string => {
  let s = text;
  // 加粗
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // 行内代码
  s = s.replace(/`([^`]+)`/g, '<code class="api-doc__md-code">$1</code>');
  // 链接
  s = s.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  return s;
};

/**
 * 轻量 Markdown 解析器：把 section.content 的 md 文本转为 HTML 字符串
 * 支持：##/###/#### 标题、> 引用块、| 表格、-/* 列表、**加粗**、`代码`、[链接](url)
 * 普通行按段落渲染，空行作为段落分隔
 */
const renderMdContent = (raw: string): string => {
  if (!raw) return "";
  const text = String(raw).replace(/\r\n/g, "\n");
  const lines = text.split("\n");
  const html: string[] = [];
  let i = 0;
  let para: string[] = [];

  // 把累积的段落行合并为 <p>
  const flushPara = () => {
    if (para.length > 0) {
      html.push(`<p>${para.map(renderMdInline).join("<br/>")}</p>`);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // 空行 → 段落分隔
    if (!trimmed) {
      flushPara();
      i++;
      continue;
    }

    // 代码块 ```lang ... ```（必须最先识别，避免内部内容被其他规则误解析）
    if (/^```[a-zA-Z]*\s*$/.test(trimmed)) {
      flushPara();
      const lang = (trimmed.match(/^```([a-zA-Z]*)/) || [])[1] || "";
      i++;
      const codeLines: string[] = [];
      while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // 跳过结束 ```
      const code = codeLines.join("\n");
      // 把 md 代码块的语言映射到高亮器支持的 SampleLang
      const mappedLang: SampleLang = /^java$/i.test(lang)
        ? "java"
        : /^python|py$/i.test(lang)
          ? "python"
          : "curl"; // curl/bash/shell/sh/json/其他统一走 curl 风格高亮
      const highlighted = highlightCode(code, mappedLang);
      html.push(
        `<pre class="api-doc__md-pre"><code class="api-doc__md-code-block">${highlighted}</code></pre>`,
      );
      continue;
    }

    // 标题 ##/###/####
    const hMatch = trimmed.match(/^(#{2,4})\s+(.+)$/);
    if (hMatch) {
      flushPara();
      const level = hMatch[1].length;
      html.push(
        `<h${level} class="api-doc__md-h${level}">${renderMdInline(
          escapeHtml(hMatch[2]),
        )}</h${level}>`,
      );
      i++;
      continue;
    }

    // 引用块 >（连续多行合并为一个 blockquote）
    if (/^>\s?/.test(trimmed)) {
      flushPara();
      const quoteLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      html.push(
        `<blockquote class="api-doc__md-quote">${quoteLines
          .map((q) => renderMdInline(escapeHtml(q)))
          .join("<br/>")}</blockquote>`,
      );
      continue;
    }

    // 表格 | a | b |（紧跟 |---|---| 分隔行才识别）
    if (
      /^\|.*\|$/.test(trimmed) &&
      i + 1 < lines.length &&
      /^\|[\s\-:|]+\|$/.test(lines[i + 1].trim())
    ) {
      flushPara();
      const headers = trimmed
        .slice(1, -1)
        .split("|")
        .map((s) => s.trim());
      i += 2; // 跳过分隔行
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
        const cells = lines[i]
          .trim()
          .slice(1, -1)
          .split("|")
          .map((s) => s.trim());
        rows.push(cells);
        i++;
      }
      let tableHtml =
        '<div class="api-doc__tbl-wrap"><table class="api-doc__tbl"><thead><tr>';
      headers.forEach(
        (h) => (tableHtml += `<th>${renderMdInline(escapeHtml(h))}</th>`),
      );
      tableHtml += "</tr></thead><tbody>";
      rows.forEach((row) => {
        tableHtml += "<tr>";
        row.forEach((cell, ci) => {
          tableHtml += `<td${
            ci === 0 ? ' class="api-doc__pn"' : ""
          }>${renderMdInline(escapeHtml(cell))}</td>`;
        });
        tableHtml += "</tr>";
      });
      tableHtml += "</tbody></table></div>";
      html.push(tableHtml);
      continue;
    }

    // 有序列表 1. xxx / 1) xxx（连续多行合并为一个 ol）
    if (/^\d+[.)]\s+/.test(trimmed)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ""));
        i++;
      }
      let listHtml = '<ol class="api-doc__md-olist">';
      items.forEach(
        (it) => (listHtml += `<li>${renderMdInline(escapeHtml(it))}</li>`),
      );
      listHtml += "</ol>";
      html.push(listHtml);
      continue;
    }

    // 无序列表 - xxx 或 * xxx（连续多行合并为一个 ul）
    if (/^[-*]\s+/.test(trimmed)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      let listHtml = '<ul class="api-doc__md-list">';
      items.forEach(
        (it) => (listHtml += `<li>${renderMdInline(escapeHtml(it))}</li>`),
      );
      listHtml += "</ul>";
      html.push(listHtml);
      continue;
    }

    // 裸 curl 命令识别：以 curl 开头，或续行以 -H/-F/-X/-d/-- 开头，或以 \ 结尾
    // 把连续的 curl 命令行合并为代码块（后端可能没用 ``` 包裹）
    if (/^curl\s/.test(trimmed) || /^curl\s*\\?\s*$/.test(trimmed)) {
      flushPara();
      const codeLines: string[] = [lines[i]];
      i++;
      while (i < lines.length) {
        const next = lines[i];
        const nextT = next.trim();
        if (!nextT) break;
        // 续行特征：以 - 开头、以 \ 结尾、或是 curl 续行参数
        if (
          /^\s*-[HFXd]-/.test(next) ||
          /^\s*--[\w-]/.test(next) ||
          /\\\s*$/.test(nextT) ||
          /^'\S*'\s*\\?\s*$/.test(nextT)
        ) {
          codeLines.push(next);
          i++;
        } else {
          break;
        }
      }
      const code = codeLines.join("\n");
      const highlighted = highlightCode(code, "curl");
      html.push(
        `<pre class="api-doc__md-pre"><code class="api-doc__md-code-block">${highlighted}</code></pre>`,
      );
      continue;
    }

    // 裸 JSON 块识别：以 { 开头的行，收集到整行仅为 } 的结束行
    // 注意：不能用"行尾包含 }"判断结束，否则 "data": {} 这种内联大括号会误触发结束
    if (/^\{/.test(trimmed)) {
      flushPara();
      const codeLines: string[] = [lines[i]];
      i++;
      // 结束条件：整行（trim 后）只是 } 或 };，避免被 "data": {} 这类内联 } 干扰
      while (i < lines.length) {
        const next = lines[i];
        codeLines.push(next);
        if (/^\s*\}\s*;?\s*$/.test(next)) {
          i++;
          break;
        }
        i++;
      }
      const code = codeLines.join("\n");
      const highlighted = highlightCode(code, "curl"); // JSON 用 curl 风格高亮（字符串/数字着色）
      html.push(
        `<pre class="api-doc__md-pre"><code class="api-doc__md-code-block">${highlighted}</code></pre>`,
      );
      continue;
    }

    // 普通行 → 累积为段落
    para.push(escapeHtml(trimmed));
    i++;
  }
  flushPara();
  return html.join("");
};

/**
 * 去除 content 首行与 section.title 重复的标题
 * 后端可能把 md 标题同时放进 title 和 content 首行，导致页面渲染两次相同标题
 */
const stripDupTitleFromContent = (content: string, title: string): string => {
  if (!content || !title) return content || "";
  const text = String(content).replace(/\r\n/g, "\n").trim();
  if (!text) return "";
  const t = title
    .trim()
    .replace(/^#+\s*/, "")
    .trim();
  if (!t) return text;
  const lines = text.split("\n");
  const firstLine = lines[0].trim();
  // 首行是 "标题文本" 或 "# 标题文本" 且与 title 相同 → 去掉首行
  const firstClean = firstLine.replace(/^#+\s*/, "").trim();
  if (firstClean === t) {
    return lines.slice(1).join("\n").trim();
  }
  return text;
};

/**
 * 对 sections 数组去重：相同 title + 相同 content 的小节只保留第一个
 * 后端可能重复返回相同的小节
 */
const dedupeSections = (sections: ApiSection[]): ApiSection[] => {
  const seen = new Set<string>();
  const result: ApiSection[] = [];
  for (const sec of sections) {
    const key = `${(sec.title || "").trim()}||${String(sec.content || "").trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(sec);
  }
  return result;
};

/**
 * HTTP 状态码 → 颜色语义 class
 * 200 绿色 / 4xx 橙色 / 5xx 红色 / 其他中性灰
 */
const httpStatusClass = (code: string): string => {
  const n = parseInt(String(code).trim(), 10);
  if (Number.isNaN(n)) return "neutral";
  if (n === 200) return "ok";
  if (n >= 500) return "err";
  if (n >= 400) return "warn";
  return "neutral";
};

/**
 * 计费说明关键词 → 颜色语义 class
 * 限时免费→绿 / 含"积分"→紫 / 含"价格|token"→橙 / 以"按"开头→蓝
 */
const billingDescClass = (desc: string): string => {
  const s = String(desc || "");
  if (/限时免费/.test(s)) return "free";
  if (/价格|token/i.test(s)) return "price";
  if (/积分/.test(s)) return "point";
  if (/^按/.test(s)) return "by";
  return "default";
};

/**
 * 渲染错误码表格单元格（4 列：HTTP 状态码 / 业务码 code / 内容 / 说明）
 * - col0：HTTP 状态码 → 彩色小圆点 + 等宽文字
 * - col1：业务码 code → 等宽 inline-code
 * - col2：内容描述 → 主文字
 * - col3：详细说明 → 次要灰
 */
const renderErrorCell = (
  val: string,
  colIdx: number,
): ReactElement | string => {
  const v = String(val ?? "");
  if (colIdx === 0) {
    return (
      <span className="api-doc__err-http-wrap">
        <span
          className={`api-doc__err-http-dot api-doc__err-http-dot--${httpStatusClass(
            v,
          )}`}
        />
        <span
          className={`api-doc__err-http api-doc__err-http--${httpStatusClass(v)}`}
        >
          {v}
        </span>
      </span>
    );
  }
  if (colIdx === 1) {
    return <code className="api-doc__err-code">{v}</code>;
  }
  if (colIdx === 2) {
    return <span className="api-doc__err-content">{v}</span>;
  }
  return <span className="api-doc__err-desc">{v}</span>;
};

/**
 * 渲染计费说明表格单元格（2 列：接口名 / 计费说明）
 * - col0：接口名 → 蓝色加粗
 * - col1：计费说明 → 根据关键词着色徽章
 */
const renderBillingCell = (
  val: string,
  colIdx: number,
): ReactElement | string => {
  const v = String(val ?? "");
  if (colIdx === 0) {
    return <span className="api-doc__bill-name">{v}</span>;
  }
  return (
    <span
      className={`api-doc__bill-desc api-doc__bill-desc--${billingDescClass(v)}`}
    >
      {v}
    </span>
  );
};

/**
 * 接口补充说明小节组件（协议型接口的非 REST 结构内容）
 * 渲染 title / content 文本 / table 表格
 * 注意：标题为"调用示例"或纯语言名（Java/cURL 等）的小节跳过，由右侧示例栏统一展示
 */
const SectionBlock: FC<{
  section: ApiSection;
  index: number;
  itemTitle?: string;
}> = ({ section, index, itemTitle }) => {
  if (isSampleSection(section.title)) return null;
  // 综合判断标题来源：section.title 优先，空时回退到父级 item.title
  const titleForMatch =
    (section.title || "").trim() || (itemTitle || "").trim();
  const tableData = normalizeSectionTable(section.table, titleForMatch);
  // 去除 content 首行与 title 重复的标题
  const cleanContent = section.content
    ? stripDupTitleFromContent(String(section.content), titleForMatch)
    : "";
  const mdHtml = cleanContent ? renderMdContent(cleanContent) : "";
  const hasContent = mdHtml.length > 0;
  if (!hasContent && !tableData) return null;
  // 纯表格型小节（错误码、计费说明）不需要标题前的紫色竖线装饰
  const isPlainTitle = /错误码|计费/.test(titleForMatch);
  // 判断表格语义类型，决定是否启用彩色单元格渲染
  // 同时兼容 section.title 和 item.title，以及表格内容特征
  const isErrorCodeTable =
    /错误码|error/i.test(titleForMatch) ||
    /业务错误码|HTTP\s*状态码/i.test(cleanContent || "");
  const isBillingTable =
    /计费|billing/i.test(titleForMatch) ||
    /计费说明|余额不足/i.test(cleanContent || "");
  const isColoredTable = isErrorCodeTable || isBillingTable;
  // 根据 table 类型分发到对应渲染函数
  const renderCell = (val: string, colIdx: number) => {
    if (isErrorCodeTable) return renderErrorCell(val, colIdx);
    if (isBillingTable) return renderBillingCell(val, colIdx);
    return val;
  };
  return (
    <div
      className={`api-doc__section-block${isPlainTitle ? " api-doc__section-block--plain" : ""}`}
      key={index}
    >
      <h4
        className={`api-doc__section-block-title${isPlainTitle ? " api-doc__section-block-title--plain" : ""}`}
      >
        {section.title}
      </h4>
      {hasContent && isColoredTable ? (
        /* 错误码/计费说明的描述文字用 notice 盒子样式展示，与其他接口的 notice 统一 */
        /* 注意：此处去掉 notice 默认的 24px 下边距，避免与下方表格之间产生过大空隙 */
        <div className="api-doc__notice api-doc__notice--inline">
          <Icon name="info" className="api-doc__notice-icon" />
          <div
            className="api-doc__md"
            dangerouslySetInnerHTML={{ __html: mdHtml }}
          />
        </div>
      ) : (
        hasContent && (
          <div
            className="api-doc__section-block-content api-doc__md"
            dangerouslySetInnerHTML={{ __html: mdHtml }}
          />
        )
      )}
      {tableData && (
        <div
          className={`api-doc__tbl-wrap${isColoredTable ? " api-doc__tbl-wrap--colored" : ""}`}
        >
          <table className="api-doc__tbl">
            <thead>
              <tr>
                {tableData.headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, ri) => (
                <tr key={ri}>
                  {tableData.colKeys.map((k, ci) => (
                    <td
                      key={k}
                      className={
                        ci === 0 && !isColoredTable ? "api-doc__pn" : ""
                      }
                    >
                      {renderCell(String(row[k] ?? ""), ci)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * 音色试听表格组件
 * 调用 GET /video/public-voice-tone/page 分页查询公共音色（已发布，一页 20 条）
 * 渲染公共音色列表（名称/语言/描述/试听），底部居中显示分页（上一页/下一页）
 * 试听按钮点击后切换播放/暂停，单例播放：新点击会停止上一个
 * 使用全局 audio 实例避免多音频同时播放
 */
let globalAudio: HTMLAudioElement | null = null;
let globalPlayingUrl: string | null = null;

// 每页条数：一页 50 条
const VOICE_PREVIEW_PAGE_SIZE = 10;

const VoicePreviewTable: FC = () => {
  // 从接口查询到的音色列表（映射为前端展示结构）
  const [rows, setRows] = useState<VoicePreviewRow[]>([]);
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  // 错误信息
  const [error, setError] = useState<string>("");
  // 当前页码（从 1 开始）
  const [pageNo, setPageNo] = useState<number>(1);
  // 总条数（用于计算总页数）
  const [total, setTotal] = useState<number>(0);
  // 当前正在播放的音频 URL，null 表示未播放
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  /** 拉取已发布的公共音色列表（按 pageNo 翻页，一页 20 条），并映射为表格展示行 */
  const fetchVoiceList = useCallback(async (page: number) => {
    setLoading(true);
    setError("");
    try {
      const res = await getPublicVoiceTonePageApi({
        pageNo: page,
        pageSize: VOICE_PREVIEW_PAGE_SIZE,
        published: true,
      });
      if (res?.code === 0 && res.data?.list) {
        // 将接口返回项映射为 VoicePreviewRow：
        // voiceName→name、soundTextValue/langCode→language、description→desc、audioUrl→demoAudioUrl（拼 TOS 前缀）
        const mapped: VoicePreviewRow[] = res.data.list
          .map((item) => {
            const demoAudioUrl = resolvePublicVoiceAudioFullUrl(item) || "";
            if (!demoAudioUrl) return null;
            // 优先使用后端返回的 soundTextValue 作为语言展示，否则用 getLanguageName 兜底
            const language =
              item.soundTextValue?.trim() || getLanguageName(item.langCode);
            return {
              name: item.voiceName || "",
              language,
              desc: item.description || "",
              demoAudioUrl,
            };
          })
          .filter((r): r is VoicePreviewRow => r !== null);
        setRows(mapped);
        setTotal(res.data.total ?? 0);
      } else {
        setRows([]);
        setTotal(0);
        setError(res?.msg || "暂无音色数据");
      }
    } catch (e: any) {
      console.error("[ApiDoc] 音色列表查询失败:", e);
      setError(e?.message || "音色列表加载失败");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // 组件挂载时拉取第一页
  useEffect(() => {
    fetchVoiceList(1);
  }, [fetchVoiceList]);

  /** 切换到上一页 */
  const handlePagePrev = () => {
    if (pageNo <= 1) return;
    const next = pageNo - 1;
    setPageNo(next);
    // 翻页后停止当前播放
    if (globalAudio) {
      globalAudio.pause();
    }
    setPlayingUrl(null);
    globalPlayingUrl = null;
    fetchVoiceList(next);
  };

  /** 切换到下一页 */
  const handlePageNext = () => {
    if (pageNo >= pageCount) return;
    const next = pageNo + 1;
    setPageNo(next);
    if (globalAudio) {
      globalAudio.pause();
    }
    setPlayingUrl(null);
    globalPlayingUrl = null;
    fetchVoiceList(next);
  };

  // 总页数：至少 1 页，避免空数据时出现 0 页
  const pageCount = Math.max(1, Math.ceil(total / VOICE_PREVIEW_PAGE_SIZE));
  const hasPrev = pageNo > 1;
  const hasNext = pageNo < pageCount;

  /** 点击试听按钮：同一个音色切换播放/暂停，不同音色切换源并播放 */
  const handleTogglePlay = (row: VoicePreviewRow) => {
    // 没有 audio 实例时创建
    if (!globalAudio) {
      globalAudio = new Audio();
      // 播放结束：清空当前播放状态
      globalAudio.addEventListener("ended", () => {
        setPlayingUrl(null);
        globalPlayingUrl = null;
      });
      // 加载/播放失败：清空当前播放状态
      globalAudio.addEventListener("error", () => {
        setPlayingUrl(null);
        globalPlayingUrl = null;
      });
    }

    // 正在播放的是同一个音色 → 暂停
    if (playingUrl === row.demoAudioUrl) {
      globalAudio.pause();
      setPlayingUrl(null);
      globalPlayingUrl = null;
      return;
    }

    // 切换到新音色
    globalAudio.src = row.demoAudioUrl;
    globalAudio.currentTime = 0;
    globalAudio
      .play()
      .then(() => {
        setPlayingUrl(row.demoAudioUrl);
        globalPlayingUrl = row.demoAudioUrl;
      })
      .catch(() => {
        setPlayingUrl(null);
        globalPlayingUrl = null;
      });
  };

  return (
    <div className="api-doc__section-block api-doc__section-block--voice-preview">
      <h4 className="api-doc__section-block-title">音色试听</h4>
      <div className="api-doc__tbl-wrap api-doc__tbl-wrap--voice-preview">
        {loading ? (
          <div className="api-doc__voice-loading">音色列表加载中…</div>
        ) : error ? (
          <div className="api-doc__voice-loading api-doc__voice-loading--error">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="api-doc__voice-loading">暂无音色数据</div>
        ) : (
          <table className="api-doc__tbl">
            <thead>
              <tr>
                <th style={{ width: "12%" }}>音色名称</th>
                <th style={{ width: "10%" }}>语言</th>
                <th>描述</th>
                <th style={{ width: "10%" }}>试听</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isPlaying = playingUrl === row.demoAudioUrl;
                return (
                  <tr key={`${row.name}-${idx}`}>
                    <td className="api-doc__pn">{row.name}</td>
                    <td>
                      <span
                        className="api-doc__voice-lang"
                        data-lang={row.language}
                      >
                        {row.language}
                      </span>
                    </td>
                    <td className="api-doc__voice-desc">{row.desc}</td>
                    <td>
                      <button
                        type="button"
                        className={`api-doc__play-btn${isPlaying ? " playing" : ""}`}
                        onClick={() => handleTogglePlay(row)}
                        aria-label={isPlaying ? "暂停" : "播放"}
                      >
                        {/* 播放/暂停图标：纯 CSS 三角形 + 两根竖线 */}
                        <span
                          className="api-doc__play-icon"
                          aria-hidden="true"
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* 分页：复用项目通用分页组件，居中显示在盒子内底部 */}
      {!loading && !error && rows.length > 0 && (
        <HomeSeriesPagination
          page={pageNo}
          pageCount={pageCount}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPagePrev={handlePagePrev}
          onPageNext={handlePageNext}
          className="api-doc__voice-pagination"
        />
      )}
    </div>
  );
};

/**
 * 接口卡片组件
 */
interface ApiCardProps {
  item: ApiItem;
  onCopy?: (text: string) => void;
}

export const ApiCard: FC<ApiCardProps> = ({ item, onCopy }) => {
  const methodClass = getMethodClass(item.method) as MethodClass;

  // 复制接口地址（与右侧代码区一致：底部 toast 提示，不替换按钮内容）
  const handleCopyPath = () => {
    if (onCopy) {
      onCopy(item.path);
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(item.path);
    }
  };

  return (
    <article
      className={`api-doc__card${item.placeholder ? " placeholder" : ""}`}
      id={item.id}
    >
      <div className="api-doc__api-hd">
        <h3 className="api-doc__api-title">{item.title}</h3>
        {/* INFO 类型（错误码、计费说明等说明性条目）不展示接口元信息 */}
        {item.method !== "INFO" && (
          <div className="api-doc__api-meta">
            <span className={`api-doc__m-badge ${methodClass}`}>
              {item.method}
            </span>
            <span className="api-doc__ep-path" title={item.path}>
              {item.path}
            </span>
            <button
              className="api-doc__icon-btn"
              title="复制接口地址"
              onClick={handleCopyPath}
            >
              <Icon name="copy" />
            </button>
          </div>
        )}
      </div>

      <p className="api-doc__api-desc">{item.desc}</p>

      {item.notice && (
        <div className="api-doc__notice">
          <Icon name="info" className="api-doc__notice-icon" />
          <div>{item.notice}</div>
        </div>
      )}

      {item.params && item.params.length > 0 && (
        <div className="api-doc__tbl-wrap">
          <h4 className="api-doc__tbl-title">
            请求参数 <span className="api-doc__t-tag">Request</span>
          </h4>
          <table className="api-doc__tbl">
            <thead>
              <tr>
                <th style={{ width: "19%" }}>名称</th>
                <th style={{ width: "15%" }}>类型</th>
                <th style={{ width: "12%" }}>必填</th>
                <th style={{ width: "14%" }}>默认值</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              {item.params.map((row, idx) => (
                <tr key={idx}>
                  <td className="api-doc__pn">{row.name}</td>
                  <td>
                    <span className="api-doc__pt" data-type={row.type}>
                      {row.type}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`api-doc__rq ${
                        isRequired(row.required) ? "y" : "n"
                      }`}
                    >
                      {row.required}
                    </span>
                  </td>
                  <td>{row.default}</td>
                  <td>{row.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {item.responses && item.responses.length > 0 && (
        <div className="api-doc__tbl-wrap">
          <h4 className="api-doc__tbl-title">
            响应结构 <span className="api-doc__t-tag">Response</span>
          </h4>
          <table className="api-doc__tbl">
            <thead>
              <tr>
                <th style={{ width: "28%" }}>字段</th>
                <th style={{ width: "18%" }}>类型</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              {item.responses.map((row, idx) => (
                <tr key={idx}>
                  <td className="api-doc__pn">{row.field}</td>
                  <td>
                    <span className="api-doc__pt" data-type={row.type}>
                      {row.type}
                    </span>
                  </td>
                  <td>{row.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 补充说明小节：协议型接口的非 REST 结构内容（接口地址/认证方式/连接步骤/Event 定义/Proto 文件等） */}
      {/* 渲染前去重，避免后端重复返回相同小节 */}
      {item.sections && item.sections.length > 0 && (
        <div
          className={`api-doc__sections${
            item.method === "INFO" ? " api-doc__sections--plain" : ""
          }`}
        >
          {dedupeSections(item.sections).map((section, idx) => (
            <SectionBlock
              section={section}
              index={idx}
              key={idx}
              itemTitle={item.title}
            />
          ))}
        </div>
      )}

      {/* 音色试听：仅在"音色列表查询"接口注入，数据来自公共音色分页接口 */}
      {item.title && item.title.includes("音色列表") && <VoicePreviewTable />}
    </article>
  );
};

/**
 * 主 API 文档页组件
 * 三栏布局：左侧导航 + 中间内容 + 右侧代码示例
 * 数据来自后端 /points/api-source-doc/current 接口
 */
export const ApiDocPage: FC = () => {
  // 接口文档数据
  const [docData, setDocData] = useState<ApiDocData | null>(null);
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  // 错误信息
  const [error, setError] = useState<string>("");

  // 当前选中的接口 id
  const [activeId, setActiveId] = useState<string>("");
  // 当前代码语言
  const [curLang, setCurLang] = useState<SampleLang>("python");
  // 搜索关键词
  const [searchQuery, setSearchQuery] = useState<string>("");
  // 折叠的分组 id 集合
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  // toast 提示文本
  const [toastText, setToastText] = useState<string>("");
  // 移动端左侧导航抽屉开关（≤960px 生效）
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  // 移动端右侧代码栏开关（≤1024px 生效）
  const [mobileCodeOpen, setMobileCodeOpen] = useState<boolean>(false);

  // 顶部文档标签：api = 接口文档（保持原有逻辑不变）/ skill = 技能文档
  const [activeTab, setActiveTab] = useState<"api" | "skill">("api");

  // 内容区滚动容器引用
  const contentRef = useRef<HTMLDivElement | null>(null);
  // 滚动节流定时器
  const scrollTimerRef = useRef<number | null>(null);
  // 点击导航触发的滚动锁，防止滚动动画期间 handleScroll 覆盖 activeId
  const scrollLockRef = useRef(false);

  // 设置页面标题
  useEffect(() => {
    document.title = "API 文档 - Lychee Voice";
  }, []);

  // 拉取接口文档数据
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      // 接口文档已开放为公开页面，未登录也可直接查看，无需鉴权
      setLoading(true);
      setError("");
      try {
        const res = (await getApiSourceDocApi()) as any;
        // 调试日志：查看后端实际返回内容
        console.log("[ApiDoc] 接口返回:", {
          code: res?.code,
          msg: res?.msg,
          hasData: !!res?.data,
        });
        if (cancelled) return;
        // 兼容多种成功状态码：200 / 0
        if ((res.code === 200 || res.code === 0) && res.data) {
          // 后端 data 是文档元信息，真正的渲染 JSON 在 data.content 字段（字符串，需 JSON.parse）
          const raw = res.data;
          let parsed: ApiDocData | null = null;
          // 1) content 是字符串 → JSON.parse
          if (typeof raw.content === "string" && raw.content.trim()) {
            try {
              parsed = JSON.parse(raw.content);
            } catch (e) {
              console.error(
                "[ApiDoc] content JSON.parse 失败:",
                e,
                raw.content.slice(0, 200),
              );
              setError("接口文档内容解析失败（content 不是合法 JSON）");
              setLoading(false);
              return;
            }
          }
          // 2) 兜底：如果 content 不存在但 data 本身就是 ApiDocData 结构，直接用
          if (!parsed && raw.groups) {
            parsed = raw as unknown as ApiDocData;
          }
          if (!parsed) {
            setError("接口文档内容为空");
            setLoading(false);
            return;
          }
          // 数据规范化：确保每个 group 和 item 都有全局唯一 id
          // 没有唯一 id 会导致滚动联动、active 高亮、右侧代码切换全部失效
          // 后端可能为多个接口返回相同 id（如同名"查询任务状态"在不同分组下），
          // 这里强制去重：重复 id 追加 -序号 后缀
          if (parsed.groups?.length) {
            const usedIds = new Set<string>();
            const usedGroupIds = new Set<string>();
            parsed.groups = parsed.groups.map((g, gi) => {
              // 分组 id 去重
              let groupId = g.id || `grp-${gi}`;
              while (usedGroupIds.has(groupId)) {
                groupId = `${groupId}-${gi}`;
              }
              usedGroupIds.add(groupId);
              // group.id 也加入 usedIds，避免后续 item.id 与 group.id 冲突
              // 冲突会导致点击菜单跳到分组大标题而非接口卡片
              usedIds.add(groupId);
              return {
                ...g,
                id: groupId,
                items: (g.items || []).map((it, ii) => {
                  // 接口 id 去重：同名同 id 的接口追加分组序号+项序号后缀
                  let itemId = it.id || `api-${gi}-${ii}`;
                  while (usedIds.has(itemId)) {
                    itemId = `${it.id || `api-${gi}`}-${ii}-${Math.random().toString(36).slice(2, 6)}`;
                  }
                  usedIds.add(itemId);
                  return { ...it, id: itemId };
                }),
              };
            });
          }
          console.log("[ApiDoc] 规范化后数据:", {
            groupCount: parsed.groups?.length,
            firstGroup: parsed.groups?.[0]?.title,
            firstItemId: parsed.groups?.[0]?.items?.[0]?.id,
            firstItemTitle: parsed.groups?.[0]?.items?.[0]?.title,
            hasSample: !!parsed.groups?.[0]?.items?.[0]?.sample,
          });
          setDocData(parsed);
          // 默认折叠所有分组
          if (parsed.groups?.length) {
            setCollapsedGroups(new Set(parsed.groups.map((g) => g.id)));
          }
          // 默认选中"快速开始"菜单项
          setActiveId("quick-start");
        } else {
          setError(res.msg || "接口文档加载失败");
        }
      } catch (err: any) {
        if (cancelled) return;
        // 调试日志：查看 catch 到的错误详情
        console.log("[ApiDoc] 请求异常:", {
          message: err?.message,
          status: err?.status,
          msg: err?.msg,
          response: err?.response,
        });
        const msg = err instanceof Error ? err.message : "网络异常";
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  // 所有接口扁平化（用于查找当前接口对象）
  const allItems = useMemo(() => {
    const result: Array<ApiItem & { groupTitle: string; groupId: string }> = [];
    docData?.groups.forEach((group: ApiGroup) => {
      group.items.forEach((item: ApiItem) => {
        result.push({
          ...item,
          groupTitle: group.title,
          groupId: group.id,
        });
      });
    });
    return result;
  }, [docData]);

  // 当前选中的接口对象
  const activeItem = useMemo(
    () => allItems.find((item) => item.id === activeId) || null,
    [allItems, activeId],
  );

  // 通过 itemId 在数据里查找所属 groupId（数据驱动，不依赖 DOM）
  const findGroupIdByItemId = useCallback(
    (itemId: string): string | null => {
      for (const group of docData?.groups || []) {
        if (group.items.some((it) => it.id === itemId)) {
          return group.id;
        }
      }
      return null;
    },
    [docData],
  );

  // 展开指定接口所属的分组（数据驱动：从数据里查 groupId，再从 collapsedGroups 移除）
  const expandGroupOfItem = useCallback(
    (itemId: string) => {
      // 快速开始属于 API 指引分组，特殊处理
      if (itemId === "quick-start") {
        setCollapsedGroups((prev) => {
          // 找到 API 指引分组并展开
          const guideGroup = docData?.groups.find(
            (g) => /api|指引/i.test(g.id) || /API\s*指引/.test(g.title),
          );
          if (guideGroup && prev.has(guideGroup.id)) {
            const next = new Set(prev);
            next.delete(guideGroup.id);
            return next;
          }
          return prev;
        });
        return;
      }
      const groupId = findGroupIdByItemId(itemId);
      if (!groupId) return;
      setCollapsedGroups((prev) => {
        if (!prev.has(groupId)) return prev; // 已展开，无需更新
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    },
    [findGroupIdByItemId, docData],
  );

  // 滚动到指定接口
  // 加锁防止滚动动画期间 handleScroll 根据中途位置覆盖 activeId
  // 智能定位：目标卡片无法滚到顶部（如下方无填充内容）时，滚到最接近顶部的位置
  // 智能查找：优先匹配内容区内的接口卡片（article.api-doc__card），
  // 避免 document.getElementById 拿到同 id 的分组 section（大标题），
  // 解决"点击语音合成却跳到语音&音色分组大标题"的问题
  const scrollToSection = useCallback(
    (id: string) => {
      const content = contentRef.current;
      // 先在内容区里精确查找接口卡片，回退到全局 getElementById
      const target =
        (content?.querySelector<HTMLElement>(
          `article.api-doc__card[id="${CSS.escape(id)}"]`,
        ) as HTMLElement | null) || document.getElementById(id);
      if (target) {
        scrollLockRef.current = true;
        // 统一用 content.scrollTo 控制滚动位置，向上多留 50px 顶部偏移，
        // 避免目标卡片贴顶时上一个接口的尾部内容仍残留在视口里
        if (content) {
          const contentRect = content.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          // 目标卡片顶部相对内容区的偏移量
          const offset = targetRect.top - contentRect.top + content.scrollTop;
          // 最大可滚动距离
          const maxScroll = content.scrollHeight - content.clientHeight;
          // 期望滚动位置：目标顶部 - 50px（留出顶部呼吸空间）
          const desired = Math.max(0, offset - 30);
          // 不能超过最大可滚动距离
          const finalTop = Math.min(desired, maxScroll);
          content.scrollTo({
            top: finalTop,
            behavior: "smooth",
          });
        } else {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        // 平滑滚动动画通常 300-500ms，800ms 后解锁足够覆盖
        window.setTimeout(() => {
          scrollLockRef.current = false;
        }, 800);
      }
      setActiveId(id);
      expandGroupOfItem(id);
    },
    [expandGroupOfItem],
  );

  // 点击左侧导航项
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    e.preventDefault();
    scrollToSection(id);
    // 移动端点击导航后自动收起抽屉
    setMobileNavOpen(false);
  };

  // 处理内容区滚动，自动高亮当前接口 + 展开所属分组
  // 用 getBoundingClientRect 相对于 content 容器判断，避免 offsetTop 受 offsetParent/折叠影响
  const handleScroll = useCallback(() => {
    // 点击导航触发的滚动动画期间跳过，避免中途位置覆盖 activeId
    if (scrollLockRef.current) return;
    if (scrollTimerRef.current) {
      window.clearTimeout(scrollTimerRef.current);
    }
    scrollTimerRef.current = window.setTimeout(() => {
      // 防抖回调执行时再次校验锁状态
      if (scrollLockRef.current) return;
      const content = contentRef.current;
      if (!content) return;
      const contentRect = content.getBoundingClientRect();
      // 以内容区顶部往下 160px 作为"当前接口"判定线
      const judgeLine = contentRect.top + 160;
      // 兜底：内容区已滚到底部时（剩余距离 < 8px），直接选中最后一个可见卡片
      // 解决最后一项卡片下方无填充内容、无法滚到判定线导致 active 被覆盖回上一项的问题
      const reachedBottom =
        content.scrollHeight - content.scrollTop - content.clientHeight < 8;
      let current = "";
      // 候选元素：接口卡片 + 快速开始 section（无 .api-doc__card class，需单独纳入）
      const cards = Array.from(
        content.querySelectorAll<HTMLElement>(".api-doc__card, #quick-start"),
      );
      if (reachedBottom && cards.length > 0) {
        // 滚到底部：从后往前找第一个在视口内的卡片
        for (let i = cards.length - 1; i >= 0; i--) {
          const rect = cards[i].getBoundingClientRect();
          if (rect.bottom > contentRect.top && rect.top < contentRect.bottom) {
            current = cards[i].id;
            break;
          }
        }
      } else {
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          // 卡片顶部在判定线以上 → 视为已滚过，记为候选
          if (rect.top <= judgeLine) {
            current = card.id;
          }
        });
      }
      if (current) {
        setActiveId(current);
        expandGroupOfItem(current);
      }
    }, 60);
  }, [expandGroupOfItem]);

  // 监听滚动
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    content.addEventListener("scroll", handleScroll);
    return () => {
      content.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current) {
        window.clearTimeout(scrollTimerRef.current);
      }
    };
  }, [handleScroll]);

  // active 项变化时，让左侧导航对应项滚动到可视区域（仅滚动导航树，不滚动整页）
  useEffect(() => {
    if (!activeId) return;
    const navTree = document.querySelector(".api-doc__tree");
    if (!navTree) return;
    const activeEl = navTree.querySelector<HTMLElement>(
      ".api-doc__t-item.active",
    );
    if (!activeEl) return;
    // block: nearest 仅在不可见时才滚动，避免与点击导航时的滚动冲突
    activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  // Ctrl+K 聚焦搜索框
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const input = document.getElementById(
          "apiDocSearchInput",
        ) as HTMLInputElement | null;
        input?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // 切换分组折叠
  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // 过滤导航树
  // 搜索过滤：匹配分组名、接口标题、路径、描述、参数名、响应字段、sections 标题/内容
  // 策略：关键词分词后逐词匹配，所有词都命中才保留（AND 逻辑），提升精准度
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return docData?.groups || [];
    // 按空格拆分多个关键词，支持 "翻译 语音" 这种组合搜索
    const keywords = q.split(/\s+/).filter(Boolean);
    if (keywords.length === 0) return docData?.groups || [];

    // 单个接口是否命中所有关键词
    const matchItem = (item: ApiItem): boolean => {
      // 预拼接接口的可搜索文本，避免每个关键词都重复拼接
      const haystack = [
        item.title,
        item.path,
        item.desc,
        item.method,
        item.notice,
        item.params?.map((p) => `${p.name} ${p.desc}`).join(" "),
        item.responses?.map((r) => `${r.field} ${r.desc}`).join(" "),
        item.sections?.map((s) => `${s.title} ${s.content || ""}`).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return keywords.every((kw) => haystack.includes(kw));
    };

    return (docData?.groups || [])
      .map((group) => {
        const groupTitle = (group.title || "").toLowerCase();
        // 分组名命中任一关键词 → 整组保留
        const groupMatched = keywords.some((kw) => groupTitle.includes(kw));
        if (groupMatched) {
          return { ...group, items: group.items };
        }
        // 否则按接口级过滤
        return {
          ...group,
          items: group.items.filter(matchItem),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [searchQuery, docData]);

  // 复制代码到剪贴板
  const copyText = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setToastText("已复制到剪贴板");
        window.setTimeout(() => setToastText(""), 2200);
      });
    }
  };

  // 当前接口可用的代码语言列表
  // 来源：item.sample 字段 + sections 中标题为语言名的小节
  const availableLangs = useMemo<Array<SampleLang>>(() => {
    if (!activeItem) return [];
    const langs: Array<SampleLang> = [];
    const s = activeItem.sample;
    const fromSample = (lang: SampleLang) => !!(s && s[lang]);
    const fromSections = (lang: SampleLang) =>
      !!sampleFromSections(activeItem, lang);
    if (fromSample("curl") || fromSections("curl")) langs.push("curl");
    if (fromSample("python") || fromSections("python")) langs.push("python");
    if (fromSample("java") || fromSections("java")) langs.push("java");
    return langs;
  }, [activeItem]);

  // 切换接口后，若当前语言不可用则自动切到第一个可用语言
  useEffect(() => {
    if (availableLangs.length === 0) return;
    if (!availableLangs.includes(curLang)) {
      setCurLang(availableLangs[0]);
    }
  }, [availableLangs, curLang]);

  // 当前接口的请求代码：优先 item.sample，其次从 sections 提取
  const requestCode = useMemo(() => {
    if (!activeItem) return "";
    const s = activeItem.sample;
    const direct = s && s[curLang] ? s[curLang] : "";
    if (direct) return direct;
    return sampleFromSections(activeItem, curLang);
  }, [activeItem, curLang]);

  // 当前接口的响应代码
  const responseCode = useMemo(() => {
    if (!activeItem?.sample) return "";
    return activeItem.sample.response || "{}";
  }, [activeItem]);

  // 加载中
  if (loading) {
    return (
      <div className="marketing marketing--light api-doc">
        <MarketingHeader edgeToEdge />
        <div className="api-doc__main">
          <div className="api-doc__loading">
            <Icon name="loader-2" className="api-doc__loading-icon" />
            <p>正在加载接口文档…</p>
          </div>
        </div>
      </div>
    );
  }

  // 加载失败
  if (error || !docData) {
    return (
      <div className="marketing marketing--light api-doc">
        <MarketingHeader edgeToEdge />
        <div className="api-doc__main">
          <div className="api-doc__error">
            <Icon name="alert-triangle" className="api-doc__error-icon" />
            <p>{error || "接口文档数据为空"}</p>
            <button
              className="api-doc__retry-btn"
              onClick={() => window.location.reload()}
            >
              重新加载
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="marketing marketing--light api-doc">
      <MarketingHeader edgeToEdge />
      {activeTab === "api" ? (
        <>
          {/* 移动端顶部工具栏：菜单按钮 + 代码示例按钮（≤960px 显示） */}
      <div className="api-doc__mobile-bar">
        <button
          type="button"
          className="api-doc__mobile-btn"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label="切换导航"
        >
          <Icon name="chevron-down" className="api-doc__mobile-btn-icon" />
          <span>目录</span>
        </button>
        <button
          type="button"
          className="api-doc__mobile-btn"
          onClick={() => setMobileCodeOpen((v) => !v)}
          aria-label="切换代码示例"
        >
          <Icon name="file-code-2" className="api-doc__mobile-btn-icon" />
          <span>示例</span>
        </button>
      </div>
      {/* 抽屉遮罩（导航或代码栏打开时显示） */}
      {(mobileNavOpen || mobileCodeOpen) && (
        <div
          className="api-doc__overlay"
          onClick={() => {
            setMobileNavOpen(false);
            setMobileCodeOpen(false);
          }}
        />
      )}
      <div className="api-doc__main">
        {/* 左侧导航 */}
        <aside
          className={`api-doc__sidebar-left${mobileNavOpen ? " mobile-open" : ""}`}
        >
          <div className="api-doc__sb-top">
            <div className="api-doc__sb-search">
              <Icon name="search" className="api-doc__si" />
              <input
                type="text"
                id="apiDocSearchInput"
                placeholder="搜索接口…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearchQuery("");
                  }
                }}
              />
              <span className="api-doc__sk">Ctrl K</span>
            </div>
          </div>
          <div className="api-doc__tree">
            {filteredGroups.map((group) => (
              <div
                className={`api-doc__grp${
                  collapsedGroups.has(group.id) && !searchQuery.trim()
                    ? " collapsed"
                    : ""
                }`}
                key={group.id}
              >
                <div
                  className="api-doc__grp-title"
                  onClick={() => toggleGroup(group.id)}
                >
                  <Icon name="chevron-down" className="api-doc__chev" />
                  <span className="api-doc__group-emoji">
                    <GroupIcon group={group} />
                  </span>
                  <span className="api-doc__group-name">{group.title}</span>
                  <span className="api-doc__group-count">
                    {group.items.length +
                      (/api|指引/i.test(group.id) ||
                      /API\s*指引/.test(group.title)
                        ? 1
                        : 0)}
                  </span>
                </div>
                <div className="api-doc__grp-items">
                  {/* API 指引分组首项前插入"快速开始"虚拟菜单项 */}
                  {/api|指引/i.test(group.id) ||
                  /API\s*指引/.test(group.title) ? (
                    <a
                      className={`api-doc__t-item api-doc__t-item--quick${
                        activeId === "quick-start" ? " active" : ""
                      }`}
                      href="#quick-start"
                      onClick={(e) => handleNavClick(e, "quick-start")}
                    >
                      <span className="api-doc__lbl">快速开始</span>
                    </a>
                  ) : null}
                  {group.items.map((item) => {
                    const isGuideGroup =
                      /api|指引/i.test(group.id) ||
                      /API\s*指引/.test(group.title);
                    // API 指引分组及 INFO 类型不展示 method 徽章
                    const showMethod = !isGuideGroup && item.method !== "INFO";
                    return (
                      <a
                        key={item.id}
                        className={`api-doc__t-item${
                          activeId === item.id ? " active" : ""
                        }${
                          showMethod
                            ? ` api-doc__t-item--${getMethodClass(item.method)}`
                            : ""
                        }`}
                        href={`#${item.id}`}
                        onClick={(e) => handleNavClick(e, item.id)}
                      >
                        <span className="api-doc__lbl">{item.title}</span>
                        {showMethod && (
                          <span className="api-doc__nav-method">
                            {item.method}
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* 中间内容 */}
        <main className="api-doc__content" ref={contentRef}>
          {/* 顶部 API / Skill 切换 Tab 栏：位于 hero 正上方 */}
          <SkillDocTabBar active={activeTab} onChange={setActiveTab} />
          {/* ===== 快速开始：引导标题 + 认证信息 ===== */}
          <section className="api-doc__quick-section" id="quick-start">
            {/* <div className="api-doc__guide-hd">
              <h2 className="api-doc__guide-title">快速开始</h2>
              <p className="api-doc__guide-subtitle">
                几分钟内完成首次 API 调用
              </p>
            </div> */}
            <section className="api-doc__hero" id="intro">
              <div className="api-doc__hero-inner">
                <span className="api-doc__hero-kicker">
                  <Icon name="sparkles" />
                  v1.0 正式版
                </span>
                <h1 className="api-doc__hero-title">Lychee API 接口文档</h1>
                <p className="api-doc__hero-subtitle">
                  提供语音克隆、语音合成、字幕翻译、视频处理等丰富的 AI
                  能力，助力开发者快速构建智能应用
                </p>
                {docData.baseUrl && (
                  <div className="api-doc__hero-baseurl">
                    <span className="api-doc__hero-baseurl-label">
                      Base URL
                    </span>
                    <code>{docData.baseUrl}</code>
                  </div>
                )}

                {/* Hero 操作按钮：创建 API Key（已登录跳工作台打开密钥弹窗，未登录跳登录页） */}
                <div className="api-doc__hero-actions">
                  <button
                    type="button"
                    className="api-doc__btn api-doc__btn-primary"
                    onClick={() => {
                      const token = localStorage.getItem("token");
                      if (token) {
                        // 已登录：跳转工作台首页，带参数触发 API 密钥弹窗
                        window.location.href =
                          "/hub/premium-translation?open_api_key=1";
                      } else {
                        // 未登录：跳转登录页，登录后回到 API 文档
                        window.location.href = `/login?redirect=${encodeURIComponent(
                          window.location.pathname + window.location.search,
                        )}`;
                      }
                    }}
                  >
                    创建 API Key
                  </button>
                </div>
                <p className="api-doc__hero-tip">
                  用于快速调用平台开放接口，完成服务鉴权
                </p>
              </div>

              {/* 统计卡片 */}
              {/* <div className="api-doc__stats">
              <div className="api-doc__stat-card">
                <div className="api-doc__stat-number">
                  {docData.groups
                    .filter(
                      (g) =>
                        !/api|指引/i.test(g.id) && !/API\s*指引/.test(g.title),
                    )
                    .reduce((sum, g) => sum + g.items.length, 0)}
                </div>
                <div className="api-doc__stat-label">核心接口</div>
              </div>
              <div className="api-doc__stat-card">
                <div className="api-doc__stat-number">
                  {
                    docData.groups.filter(
                      (g) =>
                        !/api|指引/i.test(g.id) && !/API\s*指引/.test(g.title),
                    ).length
                  }
                </div>
                <div className="api-doc__stat-label">服务类别</div>
              </div>
              <div className="api-doc__stat-card">
                <div className="api-doc__stat-number">99.9%</div>
                <div className="api-doc__stat-label">服务可用性</div>
              </div>
              <div className="api-doc__stat-card">
                <div className="api-doc__stat-number">200ms</div>
                <div className="api-doc__stat-label">平均响应</div>
              </div>
            </div> */}
            </section>

            {/* API 指引：认证信息 */}
            <section className="api-doc__auth-card" id="auth-info">
              <div className="api-doc__auth-header">
                <div className="api-doc__auth-icon">
                  <Icon name="lock" />
                </div>
                <div>
                  <h2 className="api-doc__auth-title">Base URL 与认证</h2>
                  <p className="api-doc__auth-subtitle">
                    所有接口的基础地址和认证方式
                  </p>
                </div>
              </div>
              {docData.baseUrl && (
                <div className="api-doc__auth-url">{docData.baseUrl}</div>
              )}
              <div className="api-doc__auth-methods">
                <div className="api-doc__auth-method">
                  <div className="api-doc__auth-method-label">认证方式</div>
                  <div className="api-doc__auth-method-value">api_key</div>
                </div>
                <div className="api-doc__auth-method">
                  <div className="api-doc__auth-method-label">请求头</div>
                  <div className="api-doc__auth-method-value">
                    api_key: your_api_key
                  </div>
                </div>
              </div>
            </section>
          </section>

          {/* ===== API 指引：接口概览 ===== */}
          <section className="api-doc__guide-section" id="api-overview">
            <div className="api-doc__guide-hd">
              <h2 className="api-doc__guide-title">接口概览</h2>
              <p className="api-doc__guide-subtitle">
                探索我们提供的全部 API 服务
              </p>
            </div>
            <div className="api-doc__api-groups">
              {docData.groups.map((group) => {
                const items = group.items.filter((item) => {
                  // 过滤掉 API 指引组下的说明性条目（无实际接口的纯说明项）
                  const title = (item.title || "").trim();
                  const excludeTitles = [
                    "接口概览",
                    "Base url 与认证",
                    "Base URL 与认证",
                    "错误码",
                    "计费说明",
                  ];
                  if (excludeTitles.includes(title)) return false;
                  // 过滤掉 method 为 INFO 的纯说明型条目
                  if (item.method === "INFO") return false;
                  return true;
                });
                if (items.length === 0) return null;
                return (
                  <div className="api-doc__api-group" key={group.id}>
                    <div className="api-doc__api-group-hd">
                      <span className="api-doc__api-group-icon">
                        <GroupIcon group={group} />
                      </span>
                      <span className="api-doc__api-group-title">
                        {group.title}
                      </span>
                    </div>
                    <div className="api-doc__api-group-grid">
                      {items.map((item) => (
                        <a
                          key={item.id}
                          className="api-doc__api-card"
                          href={`#${item.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            // 复用 scrollToSection：智能查找卡片 + 向上留 50px 顶部空间
                            scrollToSection(item.id);
                          }}
                        >
                          <h3 className="api-doc__api-card-title">
                            {item.title}
                          </h3>
                          {/* 描述区：外层 flex 垂直居中 + 内层 line-clamp 2 行省略 */}
                          <div className="api-doc__api-card-desc-wrap">
                            <p className="api-doc__api-card-desc">
                              {getCardDesc(item.title, item.desc)}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ===== API 指引：响应体示例 ===== */}
          <section className="api-doc__guide-section" id="response-format">
            <div className="api-doc__guide-hd">
              <h2 className="api-doc__guide-title">统一响应体</h2>
              <p className="api-doc__guide-subtitle">
                除流式接口外，所有接口响应体结构统一
              </p>
            </div>
            <div className="api-doc__response-grid">
              <div className="api-doc__response-card">
                <div className="api-doc__response-header">
                  <span className="api-doc__response-badge api-doc__response-badge-success">
                    Success
                  </span>
                  <span className="api-doc__response-title">成功响应</span>
                </div>
                <pre className="api-doc__response-code">
                  <code>
                    <span className="hl-pun">{"{"}</span>
                    {"\n  "}
                    <span className="hl-str">"code"</span>
                    <span className="hl-pun">: </span>
                    <span className="hl-num">200</span>
                    <span className="hl-pun">,</span>
                    {"\n  "}
                    <span className="hl-str">"info"</span>
                    <span className="hl-pun">: </span>
                    <span className="hl-str">"success"</span>
                    <span className="hl-pun">,</span>
                    {"\n  "}
                    <span className="hl-str">"data"</span>
                    <span className="hl-pun">: {"{}"}</span>
                    {"\n"}
                    <span className="hl-pun">{"}"}</span>
                  </code>
                </pre>
              </div>
              <div className="api-doc__response-card">
                <div className="api-doc__response-header">
                  <span className="api-doc__response-badge api-doc__response-badge-error">
                    Error
                  </span>
                  <span className="api-doc__response-title">失败响应</span>
                </div>
                <pre className="api-doc__response-code">
                  <code>
                    <span className="hl-pun">{"{"}</span>
                    {"\n  "}
                    <span className="hl-str">"code"</span>
                    <span className="hl-pun">: </span>
                    <span className="hl-num">1001</span>
                    <span className="hl-pun">,</span>
                    {"\n  "}
                    <span className="hl-str">"info"</span>
                    <span className="hl-pun">: </span>
                    <span className="hl-str">"参数错误"</span>
                    <span className="hl-pun">,</span>
                    {"\n  "}
                    <span className="hl-str">"data"</span>
                    <span className="hl-pun">: </span>
                    <span className="hl-kw">null</span>
                    {"\n"}
                    <span className="hl-pun">{"}"}</span>
                  </code>
                </pre>
              </div>
            </div>
          </section>

          {docData.groups.map((group) => (
            <section
              className="api-doc__section"
              id={group.id}
              aria-label={group.title}
              key={group.id}
            >
              <div className="api-doc__section-hd">
                <div className="api-doc__sh-icon">
                  <GroupIcon group={group} />
                </div>
                <h2 className="api-doc__section-title">{group.title}</h2>
                {/* <span className="api-doc__sh-count">
                  {group.items.length} 项
                </span> */}
              </div>
              {group.items.map((item) => (
                <ApiCard item={item} key={item.id} onCopy={copyText} />
              ))}
            </section>
          ))}
        </main>

        {/* 右侧代码示例 */}
        <aside
          className={`api-doc__sidebar-right${mobileCodeOpen ? " mobile-open" : ""}`}
        >
          <div className="api-doc__sb-r-inner">
            {!activeItem || !activeItem.sample ? (
              <div className="api-doc__ref-panel">
                <div className="api-doc__ref-header">
                  <div className="api-doc__ref-icon">
                    <Icon name="sparkles" />
                  </div>
                  <div>
                    <h4 className="api-doc__ref-title">快速参考</h4>
                    <p className="api-doc__ref-subtitle">API 调用必备信息</p>
                  </div>
                </div>

                <div className="api-doc__ref-section">
                  <div className="api-doc__ref-section-title">
                    <Icon name="lock" className="api-doc__ref-section-icon" />
                    <span>认证方式</span>
                  </div>
                  <div className="api-doc__ref-auth">
                    <div className="api-doc__ref-auth-item">
                      <span className="api-doc__ref-auth-label">Header</span>
                      <code className="api-doc__ref-auth-code">api_key</code>
                    </div>
                    <div className="api-doc__ref-auth-value">
                      <span className="api-doc__ref-auth-value-label">
                        示例
                      </span>
                      <code className="api-doc__ref-auth-value-code">
                        api_key: your_api_key
                      </code>
                    </div>
                  </div>
                </div>

                <div className="api-doc__ref-section">
                  <div className="api-doc__ref-section-title">
                    <Icon
                      name="file-code-2"
                      className="api-doc__ref-section-icon"
                    />
                    <span>cURL 模板</span>
                  </div>
                  <div className="api-doc__ref-code">
                    <pre>
                      <code>
                        <span className="hl-kw">curl</span> -X{" "}
                        <span className="hl-kw">POST</span> '
                        <span className="hl-url">
                          https://voice.lycheeai.com.cn
                        </span>
                        ' <span className="hl-pun">\</span>
                        {"\n  "}-H '<span className="hl-kw">api_key</span>:{" "}
                        <span className="hl-var">your_api_key</span>'
                        <span className="hl-pun"> \</span>
                        {"\n  "}
                        <span className="hl-kw">-F</span> '
                        <span className="hl-kw">file</span>=@xxx.mp4'
                      </code>
                    </pre>
                  </div>
                </div>

                <div className="api-doc__ref-section">
                  <div className="api-doc__ref-section-title">
                    <Icon name="info" className="api-doc__ref-section-icon" />
                    <span>响应结构</span>
                  </div>
                  <div className="api-doc__ref-code">
                    <pre>
                      <code>
                        <span className="hl-pun">{"{"}</span>
                        {"\n  "}
                        <span className="hl-str">"code"</span>
                        <span className="hl-pun">:</span>{" "}
                        <span className="hl-num">200</span>
                        <span className="hl-pun">,</span>
                        {"\n  "}
                        <span className="hl-str">"info"</span>
                        <span className="hl-pun">:</span>{" "}
                        <span className="hl-str">"success"</span>
                        <span className="hl-pun">,</span>
                        {"\n  "}
                        <span className="hl-str">"data"</span>
                        <span className="hl-pun">: {"{}"}</span>
                        {"\n"}
                        <span className="hl-pun">{"}"}</span>
                      </code>
                    </pre>
                  </div>
                </div>

                <div className="api-doc__ref-footer">
                  <p>点击左侧接口查看完整调用示例</p>
                </div>
              </div>
            ) : (
              <div className="api-doc__sample-sticky">
                <div className="api-doc__code-title">
                  <strong>{activeItem.title}</strong>
                  <span
                    className={`api-doc__m-badge ${getMethodClass(
                      activeItem.method,
                    )}`}
                  >
                    {activeItem.method}
                  </span>
                </div>
                <div className="api-doc__lang-tabs">
                  {availableLangs.map((lang) => (
                    <button
                      key={lang}
                      className={`api-doc__lang-tab${
                        curLang === lang ? " active" : ""
                      }`}
                      onClick={() => setCurLang(lang)}
                    >
                      {lang === "curl"
                        ? "cURL"
                        : lang === "python"
                          ? "Python"
                          : "Java"}
                    </button>
                  ))}
                </div>
                <div className="api-doc__code-blk">
                  <div className="api-doc__cb-hd">
                    <span>Request</span>
                    <button
                      className="api-doc__cp-btn"
                      onClick={() => copyText(requestCode)}
                    >
                      <Icon name="copy" />
                      复制
                    </button>
                  </div>
                  <ScrollableCode code={highlightCode(requestCode, curLang)} />
                </div>
                {responseCode && (
                  <div className="api-doc__code-blk">
                    <div className="api-doc__cb-hd">
                      <span>Response</span>
                      <button
                        className="api-doc__cp-btn"
                        onClick={() => copyText(responseCode)}
                      >
                        <Icon name="copy" />
                        复制
                      </button>
                    </div>
                    <ScrollableCode
                      code={highlightCode(responseCode, "java")}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* 复制提示 toast */}
      {toastText && <div className="api-doc__toast">{toastText}</div>}
        </>
      ) : (
        <SkillDocPage activeTab={activeTab} onTabChange={setActiveTab} />
      )}
    </div>
  );
};
